import React, { useEffect, useRef, useState } from "react";
import { Prompt } from "react-router-dom";
import { useMediaQuery } from "@material-ui/core";
import ZipangHeader, { MobileSignIn } from "../zipang/Header/ZipangHeader";
import Footer from "../zipang/Footer";
import ScrollFix from "../../components/ScrollFix/ScrollFix";
import { DialogProvider } from "../../common/elements/Dialog/Dialog";
import Button from "../../common/elements/Button";

// Styled Components
import {
  PageContainer,
  WizardSection,
  WizardContainer,
  StepContent,
  StepActions,
  StepIndicatorContainer,
  StepIndicatorItem,
} from "./SellAiComponents/SellAiStyledComponents";

// Sub-components
import SellAiHero from "./SellAiComponents/SellAiHero";
import WizardStep1Photos from "./SellAiComponents/WizardStep1Photos";
import WizardStep2AIRead from "./SellAiComponents/WizardStep2AIRead";
import WizardStep3Weight, {
  getCenterStoneCarats,
  getSideStoneCarats,
} from "./SellAiComponents/WizardStep3Weight";
import WizardStep4Preview from "./SellAiComponents/WizardStep4Preview";
import WizardStep5Details from "./SellAiComponents/WizardStep5Details";
import WizardStep6Price from "./SellAiComponents/WizardStep6Price";
import WizardStep5Submit from "./SellAiComponents/WizardStep5Submit";
import { isOtherJewelleryMetal } from "./SellAiComponents/WizardStep2AIRead";
import PaywallModal from "./SellAiComponents/PaywallModal";
import SuccessView from "./SellAiComponents/SuccessView";
import GoogleReCaptchaModal from "./SellAiComponents/GoogleReCaptchaModal";
import UploadedImagePreviews from "./SellAiComponents/UploadedImagePreviews";
import {
  sellAnalyzeUrl,
  sellWeightUrl,
  caratProAnalysisSaveUrl,
  caratProPaymentCreateIntentUrl,
  caratProPaymentStatusUrl,
  caratProSeeMyPriceUrl,
  awsGetUrls,
  caratProSubmissionUrl,
  convertHeicToJpgUrl,
} from "../../common/config/app.endpoints";
import { publicApi } from "../../services/ApiService";
import { current } from "@reduxjs/toolkit";

let filesObjects: { [key: string]: File[] } = {
  photo: [],
  certificate: [],
  productExcel: [],
};

const checkProductHasStone = (data: any) => {
  const itemTypeStr = (data?.itemType ?? data?.category ?? "")
    .trim()
    .toLowerCase();
  const isDiamond =
    itemTypeStr === "diamond" ||
    itemTypeStr === "diamonds" ||
    itemTypeStr.includes("diamond");
  const hasCenterStone = Boolean(
    (data?.centreStone && data.centreStone.toString().trim() !== "") ||
    (data?.centerStone && data.centerStone.toString().trim() !== "")
  );
  const hasSideStone = Boolean(
    (data?.sideStone && data.sideStone.toString().trim() !== "") ||
    (data?.sideStones && data.sideStones.toString().trim() !== "") ||
    (data?.side_stone && data.side_stone.toString().trim() !== "") ||
    (data?.side_stones && data.side_stones.toString().trim() !== "")
  );
  return (
    isDiamond ||
    hasCenterStone ||
    hasSideStone ||
    Boolean(data?.hasStone) ||
    Boolean(data?.hasStones) ||
    Boolean(data?.stone && data.stone.toString().trim() !== "") ||
    Boolean(data?.stones && data.stones.toString().trim() !== "")
  );
};

const SellAiPage = (props: any) => {
  const [currentStep, setCurrentStep] = useState(1);
  const isMobile = useMediaQuery("(max-width: 600px)");

  // State
  const [showSignIn, setShowSignIn] = useState(false);
  const [previewUrls, setPreviewUrls] = useState<any[]>([]);
  const [activeCardId, setActiveCardId] = useState<number | null>(null);
  const [cardPreviews, setCardPreviews] = useState<{ [key: number]: string }>(
    {},
  );
  const [files, setFiles] = useState({
    photo: [],
    certificate: [],
    productExcel: [],
  } as any);

  const [isExtracting, setIsExtracting] = useState(false);
  const [isMatchingSales, setIsMatchingSales] = useState(false);
  const [isPricing, setIsPricing] = useState(false);
  const [extractError, setExtractError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [actualWeight, setActualWeight] = useState("");
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [showPaymentSuccessBanner, setShowPaymentSuccessBanner] =
    useState(false);
  const [showCaptchaModal, setShowCaptchaModal] = useState(false);
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
  const [wasPaymentMade, setWasPaymentMade] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [valuationPrice, setValuationPrice] = useState(10); // TODO: Fetch from backend
  const [valuationCurrency, setValuationCurrency] = useState("USD");
  const [isPaywallLoading, setIsPaywallLoading] = useState(false);
  const [requestsCount, setRequestsCount] = useState(3); // Mocking count > 2 for testing
  const [usageData, setUsageData] = useState<any>(null);
  const [jewelryAnalysisId, setJewelryAnalysisId] = useState<string | null>(
    null,
  );
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [verifyCode, setVerifyCode] = useState(["", "", "", "", "", ""]);
  const [sellOption, setSellOption] = useState<
    "consign" | "live" | "valuation" | "none_of_the_above" | ""
  >("");
  const [askingPrice, setAskingPrice] = useState("");
  const [contactForm, setContactForm] = useState({
    name: "",
    contactType: "email",
    email: "",
    phoneCode: "+81",
    phone: "",
    message: "",
    emailAboutUpcomingAuction: false,
    usePhotoToImproveCarrotPro: false,
  });

  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [verifyTarget, setVerifyTarget] = useState<
    "email" | "phone" | "submit"
  >("submit");

  const handleContactFormChange = React.useCallback((e: any) => {
    const { name, value, type, checked } = e.target;
    const val = type === "checkbox" ? checked : value;
    setContactForm((prev) => ({ ...prev, [name]: val }));
    if (name === "email") {
      setIsEmailVerified(false);
      setWasPaymentMade(false);
      setShowPaymentSuccessBanner(false);
    }
    if (name === "phone") setIsPhoneVerified(false);
  }, []);

  const [aiData, setAiData] = useState<any>({});
  const [initialAiData, setInitialAiData] = useState<any>({});

  const formRef = useRef(null);
  const photoRef = useRef(null) as React.MutableRefObject<any>;
  const productExcelRef = useRef(null) as React.MutableRefObject<any>;
  const certificateRef = useRef(null) as React.MutableRefObject<any>;

  useEffect(() => {
    // Show captcha modal on initial load as requested
    if (sessionStorage.getItem("skipCaptcha") === "true") {
      setIsCaptchaVerified(true);
      sessionStorage.removeItem("skipCaptcha");
    } else {
      setShowCaptchaModal(true);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    const checkPendingAnalysis = async () => {
      const pendingAnalysisStr = localStorage.getItem("pendingAnalysis");
      if (pendingAnalysisStr) {
        try {
          const pending = JSON.parse(pendingAnalysisStr);
          if (pending.jewelryAnalysisId && pending.email) {
            setJewelryAnalysisId(pending.jewelryAnalysisId);
            setContactForm((prev) => ({ ...prev, email: pending.email }));

            // Check payment status using curl_2
            try {
              const res = await publicApi.post(
                caratProSeeMyPriceUrl,
                {
                  email: pending.email,
                  draftId: pending.draftId || pending.jewelryAnalysisId || "",
                  jewelryAnalysisId: pending.jewelryAnalysisId,
                },
                {
                  timeout: 600000,
                },
              );

              if (res.status === 200) {
                setAiData((prev: any) => ({
                  ...prev,
                  estimatedMarketPriceUSD: res.data.averagePrice,
                  minPrice: res.data.minPrice,
                  maxPrice: res.data.maxPrice,
                  meltValueUSD: res.data.meltValueUSD,
                  comparableSalesCount: res.data.matchedCount,
                  confidence: res.data.confidence,
                  dataFromYear: res.data.dataFromYear,
                  dataToYear: res.data.dataToYear,
                  spotPricePerGramUsd: res.data.spotPricePerGramUsd,
                  spotPriceUpdateDate: res.data.spotPriceUpdateDate,
                  metalPurityPercentage: res.data.metalPurityPercentage,
                  metalWeight:
                    res.data.metalWeight ??
                    res.data.metalWeightInGrams ??
                    prev.metalWeight,
                }));
                setUsageData((prev: any) => ({
                  ...prev,
                  limitExceeded: false,
                }));
                localStorage.removeItem("pendingAnalysis");
              } else {
                localStorage.removeItem("pendingAnalysis");
              }
            } catch (error) {
              const err = error as any;
              if (err.response?.status === 402) {
                setUsageData((prev: any) => ({ ...prev, limitExceeded: true }));
              }
              localStorage.removeItem("pendingAnalysis");
            }
          } else {
            localStorage.removeItem("pendingAnalysis");
          }
        } catch (e) {
          localStorage.removeItem("pendingAnalysis");
        }
      }
    };
    checkPendingAnalysis();
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (currentStep > 1 && !showSuccessModal) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [currentStep, showSuccessModal]);

  const handleCaptchaSuccess = () => {
    setShowCaptchaModal(false);
    setIsCaptchaVerified(true);
  };

  const isMetalChanged = () =>
    (aiData?.metal || "").trim().toLowerCase() !==
    (initialAiData?.metal || "").trim().toLowerCase();

  const handleNext = async () => {
    if (currentStep === 1) {
      const photos = filesObjects.photo || [];
      const certificates = filesObjects.certificate || [];

      if (photos.length + certificates.length < 2) {
        setExtractError("Please upload at least 2 media before proceeding.");
        return;
      }

      if (!isCaptchaVerified) {
        setShowCaptchaModal(true);
        return;
      }

      setIsExtracting(true);
      setExtractError(null);

      const formData = new FormData();
      if (photos && photos.length > 0) {
        photos.forEach((photo) => {
          formData.append("images", photo);
        });
      }
      if (certificates && certificates.length > 0) {
        certificates.forEach((cert) => {
          formData.append("images", cert); // Sending certificates in the payload
        });
      }

      try {
        const response = await publicApi.post(sellAnalyzeUrl, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          timeout: 600000, // 10 minutes timeout for AI processing
        });
        if (response.status === 200 || response.status === 201) {
          if (response.data.draftId) {
            setJewelryAnalysisId(response.data.draftId);
          } else if (response.data.jewelryAnalysisId) {
            setJewelryAnalysisId(response.data.jewelryAnalysisId);
          } else if (response.data.id) {
            setJewelryAnalysisId(response.data.id);
          }

          const hasCenterStoneInit = Boolean(response.data.centreStone || response.data.centerStone);
          const mappedData = {
            ...response.data,
            sideStone: response.data.sideStoneType ?? response.data.sideStone,
            sideStoneColor: response.data.sideStoneColour ?? response.data.sideStoneColor,
            IndividualCaratBreakdown: response.data.individualCaratBreakdown ?? response.data.IndividualCaratBreakdown,
            centreStoneEnabled: hasCenterStoneInit,
          };

          setAiData((prev: any) => ({
            ...prev,
            ...mappedData,
            sideStoneEnabled: undefined,
            originConfirmed: false,
            origin: false,
            originType: "",
          }));
          setInitialAiData({ ...mappedData });

          if (response.data.weight) {
            const parsedWeight = parseFloat(response.data.weight);
            if (!isNaN(parsedWeight)) {
              setActualWeight(parsedWeight.toString());
            }
          }

          setCurrentStep(2); // Only move to the next step if successful
        } else {
          console.error("Failed to analyze photo:", response.data);
          setExtractError(
            response.data?.message ||
            "Failed to analyze images. Please try again or fill manually.",
          );
        }
      } catch (error) {
        const err = error as any;
        const errData = err.response?.data;
        const errMsg =
          typeof errData === "string"
            ? errData
            : errData?.message ||
            errData?.errors?.[0]?.message ||
            "We couldn't analyze your photos at this moment. Please try again.";
        setExtractError(errMsg);
      } finally {
        setIsExtracting(false);
      }
    } else if (currentStep === 2) {
      const itemTypeStr = (aiData?.itemType ?? aiData?.category ?? "")
        .trim()
        .toLowerCase();
      const isDiamond =
        itemTypeStr === "diamond" ||
        itemTypeStr === "diamonds" ||
        itemTypeStr.includes("diamond");
      const hasCenterStone = Boolean(aiData?.centreStoneEnabled);
      const hasStone = checkProductHasStone(aiData);
      const missingFields: string[] = [];

      if (isDiamond) {
        if (!aiData?.itemType?.trim()) missingFields.push("Item Type");
        if (!aiData?.carat?.toString()?.trim())
          missingFields.push("Carat (Centre)");
        if (!aiData?.shape?.trim()) missingFields.push("Shape");
        if (!aiData?.cut?.trim()) missingFields.push("Cut");
        if (!aiData?.color?.trim()) missingFields.push("Colour");
        if (!aiData?.clarity?.trim()) missingFields.push("Clarity");
      } else if (hasCenterStone) {
        if (!aiData?.itemType?.trim()) missingFields.push("Item Type");
        if (!aiData?.metal?.trim()) missingFields.push("Metal");
        if (!isOtherJewelleryMetal(aiData?.metal) && !aiData?.metalPurity?.trim()) missingFields.push("Metal Purity");
        if (!aiData?.centreStone?.trim()) missingFields.push("Centre Stone");
        if (!aiData?.carat?.toString()?.trim())
          missingFields.push("Carat (Centre)");
        if (!aiData?.shape?.trim()) missingFields.push("Shape");
        if (!aiData?.cut?.trim()) missingFields.push("Cut");
        if (!aiData?.color?.trim()) missingFields.push("Colour");
        if (!aiData?.clarity?.trim()) missingFields.push("Clarity");
      } else {
        if (!aiData?.itemType?.trim()) missingFields.push("Item Type");
        if (!aiData?.metal?.trim()) missingFields.push("Metal");
        if (!isOtherJewelleryMetal(aiData?.metal) && !aiData?.metalPurity?.trim()) missingFields.push("Metal Purity");
      }
      const hasSideStoneData = Boolean(
        (aiData?.sideStone && aiData.sideStone.toString().trim() !== "") ||
        (aiData?.sideStoneShape && aiData.sideStoneShape.toString().trim() !== "") ||
        (aiData?.sideStoneCut && aiData.sideStoneCut.toString().trim() !== "") ||
        (aiData?.sideStoneColor && aiData.sideStoneColor.toString().trim() !== "") ||
        (aiData?.sideStoneClarity && aiData.sideStoneClarity.toString().trim() !== "") ||
        (aiData?.sideStoneCarat !== undefined && aiData?.sideStoneCarat !== null && aiData.sideStoneCarat.toString().trim() !== "" && Number(aiData.sideStoneCarat) !== 0) ||
        (aiData?.noOfSideStones !== undefined && aiData?.noOfSideStones !== null && aiData.noOfSideStones.toString().trim() !== "" && Number(aiData.noOfSideStones) !== 0)
      );
      const isSideStoneEnabled = aiData?.sideStoneEnabled !== undefined ? aiData.sideStoneEnabled : hasSideStoneData;

      if (isSideStoneEnabled) {
        const checkArr = (val: any) => {
          if (val === undefined || val === null || val === "") return false;
          if (Array.isArray(val)) return val.length > 0 && val.every(v => v?.toString().trim() !== "");
          return val.toString().trim() !== "";
        };

        if (!checkArr(aiData?.sideStone)) missingFields.push("Side Stone Type");
        if (!checkArr(aiData?.sideStoneShape)) missingFields.push("Side Shape");
        if (!checkArr(aiData?.sideStoneCut)) missingFields.push("Side Cut");
        if (!checkArr(aiData?.sideStoneCarat)) missingFields.push("Side Carat");
        if (!checkArr(aiData?.sideStoneColor)) missingFields.push("Side Colour");
        if (!checkArr(aiData?.sideStoneClarity)) missingFields.push("Side Clarity");
        if (!checkArr(aiData?.noOfSideStones)) missingFields.push("Side Count");
      }

      if (hasStone && !aiData?.originConfirmed) {
        missingFields.push("Origin");
      }

      if (missingFields.length > 0) {
        setValidationErrors(missingFields);
        return;
      }
      setValidationErrors([]);

      setIsMatchingSales(true);
      setExtractError(null);

      try {
        let colorToSend = aiData.color;
        if (aiData.color) {
          if (aiData.color.toUpperCase() === "E-M") {
            colorToSend = aiData.color;
          } else {
            colorToSend = aiData.color.includes("-")
              ? aiData.color.split("-")[0].trim()
              : aiData.color.includes("/")
                ? aiData.color.split("/")[0].trim()
                : aiData.color;
          }
        }

        const payload: any = {};
        if (jewelryAnalysisId) payload.jewelryAnalysisId = jewelryAnalysisId;
        else if (aiData?.draftId) payload.jewelryAnalysisId = aiData.draftId;
        if (aiData.itemType) payload.itemType = aiData.itemType;
        if (aiData.metal) payload.metal = aiData.metal;
        if (aiData.subMetal) payload.subMetal = aiData.subMetal;
        if (aiData.metalWeight) payload.metalWeight = parseFloat(aiData.metalWeight);
        if (aiData.metalPurity) payload.metalPurity = aiData.metalPurity;
        payload.isMetalChange = isMetalChanged();
        if (aiData?.itemType?.toLowerCase().includes("diamond") || aiData?.category?.toLowerCase().includes("diamond") || aiData.centreStoneEnabled) {
          if (aiData.carat) payload.carat = parseFloat(aiData.carat);
          if (aiData.centreStone) payload.centreStone = aiData.centreStone;
          if (aiData.shape) payload.shape = aiData.shape;
          if (aiData.cut) payload.cut = aiData.cut;
          if (colorToSend) payload.color = colorToSend;
          if (aiData.clarity) payload.clarity = aiData.clarity;
        } else {
          payload.carat = null;
          payload.centreStone = null;
          payload.shape = null;
          payload.cut = null;
          payload.color = null;
          payload.clarity = null;
        }
        if (aiData.originType) payload.originType = aiData.originType;

        const toArray = (val: any) => {
          if (val === undefined || val === null || val === "") return undefined;
          if (Array.isArray(val)) return val;
          if (typeof val === 'string') return val.split(',').map((s: string) => s.trim());
          return [val];
        };

        if (isSideStoneEnabled) {
          if (aiData.sideStone) payload.sideStoneType = toArray(aiData.sideStone);
          if (aiData.sideStoneShape) payload.sideStoneShape = toArray(aiData.sideStoneShape);
          if (aiData.sideStoneCut) payload.sideStoneCut = toArray(aiData.sideStoneCut);
          if (aiData.sideStoneColor) payload.sideStoneColour = toArray(aiData.sideStoneColor);
          if (aiData.sideStoneClarity) payload.sideStoneClarity = toArray(aiData.sideStoneClarity);
          if (aiData.sideStoneCarat !== undefined && aiData.sideStoneCarat !== null && aiData.sideStoneCarat !== "") {
            payload.sideStoneCarat = Array.isArray(aiData.sideStoneCarat) ? parseFloat(aiData.sideStoneCarat[0]) : parseFloat(aiData.sideStoneCarat);
          }
          if (aiData.noOfSideStones !== undefined && aiData.noOfSideStones !== null && aiData.noOfSideStones !== "") {
            payload.noOfSideStones = Array.isArray(aiData.noOfSideStones) ? parseInt(aiData.noOfSideStones[0], 10) : parseInt(aiData.noOfSideStones, 10);
          }
          if (aiData.IndividualCaratBreakdown && aiData.IndividualCaratBreakdown.length > 0) {
            payload.individualCaratBreakdown = aiData.IndividualCaratBreakdown;
          } else if (aiData.sideStone) {
            const sideStonesArr = toArray(aiData.sideStone) || [];
            const sideShapeArr = toArray(aiData.sideStoneShape) || [];
            const sideCutArr = toArray(aiData.sideStoneCut) || [];
            const sideColorArr = toArray(aiData.sideStoneColor) || [];
            const sideClarityArr = toArray(aiData.sideStoneClarity) || [];

            const breakdown = sideStonesArr.map((type: string, idx: number) => ({
              type: type,
              shape: sideShapeArr[idx] || null,
              cut: sideCutArr[idx] || null,
              color: sideColorArr[idx] || null,
              clarity: sideClarityArr[idx] || null,
              carat: idx === 0 && aiData.sideStoneCarat ? parseFloat(Array.isArray(aiData.sideStoneCarat) ? aiData.sideStoneCarat[0] : aiData.sideStoneCarat) : null,
              count: idx === 0 && aiData.noOfSideStones ? parseInt(Array.isArray(aiData.noOfSideStones) ? aiData.noOfSideStones[0] : aiData.noOfSideStones, 10) : null,
            }));
            payload.individualCaratBreakdown = breakdown;
          }
        } else {
          payload.sideStoneType = null;
          payload.sideStoneShape = null;
          payload.sideStoneCut = null;
          payload.sideStoneColour = null;
          payload.sideStoneClarity = null;
          payload.sideStoneCarat = null;
          payload.noOfSideStones = null;
          payload.individualCaratBreakdown = [];
        }

        if (aiData.certificateAndReportNumber) {
          payload.certificateAndReportNumber = aiData.certificateAndReportNumber;
        }

        const response = await publicApi.post(
          sellWeightUrl,
          payload,
          { timeout: 600000 },
        );
        if (response.status === 200) {
          if (response.data?.estimatedWeightInGrams) {
            setActualWeight(response.data.estimatedWeightInGrams.toString());
          }
          setAiData((prev: any) => ({
            ...prev,
            weightReasoning: response.data.reasoning,
            spotPricePerGramUsd:
              response.data.spotPricePerGramUsd ?? prev.spotPricePerGramUsd,
            spotPriceUpdateDate:
              response.data.spotPriceUpdateDate ?? prev.spotPriceUpdateDate,
            metalPurityPercentage:
              response.data.metalPurityPercentage ??
              prev.metalPurityPercentage,
            grossWeightInGrams: response.data.grossWeightInGrams,
            netWeightInGrams: response.data.netWeightInGrams,
            centerStoneWeightInGrams: response.data.centerStoneWeightInGrams,
            sideStoneWeightInGrams: response.data.sideStoneWeightInGrams,
            estimatedWeightInGrams: response.data.estimatedWeightInGrams,
          }));
        }
      } catch (error) {
        console.error("Failed to fetch new weight:", error);
        const err = error as any;
        const errData = err.response?.data;
        const errMsg =
          typeof errData === "string"
            ? errData
            : errData?.message ||
            errData?.errors?.[0]?.message ||
            "Failed to confirm details. Please check your inputs and try again.";
        setExtractError(errMsg);
        setIsMatchingSales(false);
        return;
      }

      setIsMatchingSales(false);
      setCurrentStep(3); // Shorter timeout if API call took time
    } else if (currentStep === 3) {
      const itemTypeStr = (aiData?.itemType ?? aiData?.category ?? "")
        .trim()
        .toLowerCase();
      const isDiamond =
        itemTypeStr === "diamond" ||
        itemTypeStr === "diamonds" ||
        itemTypeStr.includes("diamond");
      const missingFields: string[] = [];

      const parsedWeight = parseFloat(actualWeight);
      if (isDiamond) {
        if (!actualWeight || isNaN(parsedWeight) || parsedWeight <= 0)
          missingFields.push("Gross Weight");
      } else {
        if (!actualWeight || isNaN(parsedWeight) || parsedWeight < 1)
          missingFields.push("Gross Weight");
      }

      if (missingFields.length > 0) {
        setValidationErrors(missingFields);
        return;
      }
      setValidationErrors([]);
      setCurrentStep(4);
    } else if (currentStep === 4) {
      setCurrentStep(5);
    } else if (currentStep === 5) {
      setIsPricing(true);
      const proceedToNext = () => {
        setIsPricing(false);
        setCurrentStep(6);
      };

      const grossNum = parseFloat(actualWeight) || 0;
      const centerStoneCarats = getCenterStoneCarats(aiData);
      const sideStoneCarats = getSideStoneCarats(aiData);
      const aiGrossWeight = parseFloat(aiData?.grossWeightInGrams ?? aiData?.estimatedWeightInGrams ?? aiData?.grossWeight ?? 0);
      const aiNetWeight = aiData?.netWeightInGrams !== undefined && aiData?.netWeightInGrams !== null ? parseFloat(aiData.netWeightInGrams) : null;

      const isUnchanged = !isNaN(grossNum) && !isNaN(aiGrossWeight) && grossNum === aiGrossWeight;

      const netNum = isUnchanged && aiNetWeight !== null
        ? aiNetWeight
        : (grossNum > 0
          ? Math.max(
            0,
            grossNum - centerStoneCarats * 0.2 - sideStoneCarats * 0.2,
          )
          : null);

      const hasSideStoneData = Boolean(
        (aiData?.sideStone && aiData.sideStone.toString().trim() !== "") ||
        (aiData?.sideStoneShape && aiData.sideStoneShape.toString().trim() !== "") ||
        (aiData?.sideStoneCut && aiData.sideStoneCut.toString().trim() !== "") ||
        (aiData?.sideStoneColor && aiData.sideStoneColor.toString().trim() !== "") ||
        (aiData?.sideStoneClarity && aiData.sideStoneClarity.toString().trim() !== "") ||
        (aiData?.sideStoneCarat !== undefined && aiData?.sideStoneCarat !== null && aiData.sideStoneCarat.toString().trim() !== "" && Number(aiData.sideStoneCarat) !== 0) ||
        (aiData?.noOfSideStones !== undefined && aiData?.noOfSideStones !== null && aiData.noOfSideStones.toString().trim() !== "" && Number(aiData.noOfSideStones) !== 0)
      );
      const isSideStoneEnabled = aiData?.sideStoneEnabled !== undefined ? aiData.sideStoneEnabled : hasSideStoneData;

      let sideStoneFields: any = {};
      if (isSideStoneEnabled) {
        let defaultBreakdown = null;
        if (!aiData.IndividualCaratBreakdown || aiData.IndividualCaratBreakdown.length === 0) {
          if (aiData.sideStone) {
            const sideStonesArr = Array.isArray(aiData.sideStone) ? aiData.sideStone : [aiData.sideStone];
            const sideShapeArr = Array.isArray(aiData.sideStoneShape) ? aiData.sideStoneShape : (aiData.sideStoneShape ? [aiData.sideStoneShape] : []);
            const sideCutArr = Array.isArray(aiData.sideStoneCut) ? aiData.sideStoneCut : (aiData.sideStoneCut ? [aiData.sideStoneCut] : []);
            const sideColorArr = Array.isArray(aiData.sideStoneColor) ? aiData.sideStoneColor : (aiData.sideStoneColor ? [aiData.sideStoneColor] : []);
            const sideClarityArr = Array.isArray(aiData.sideStoneClarity) ? aiData.sideStoneClarity : (aiData.sideStoneClarity ? [aiData.sideStoneClarity] : []);
            defaultBreakdown = sideStonesArr.map((type: string, idx: number) => ({
              type: type,
              shape: sideShapeArr[idx] || null,
              cut: sideCutArr[idx] || null,
              color: sideColorArr[idx] || null,
              clarity: sideClarityArr[idx] || null,
              carat: idx === 0 && aiData.sideStoneCarat ? parseFloat(Array.isArray(aiData.sideStoneCarat) ? aiData.sideStoneCarat[0] : aiData.sideStoneCarat) : null,
              count: idx === 0 && aiData.noOfSideStones ? parseInt(Array.isArray(aiData.noOfSideStones) ? aiData.noOfSideStones[0] : aiData.noOfSideStones, 10) : null,
            }));
          }
        }

        sideStoneFields = {
          sideStoneType: Array.isArray(aiData.sideStone) ? aiData.sideStone : (aiData.sideStone ? [aiData.sideStone] : []),
          sideStoneShape: Array.isArray(aiData.sideStoneShape) ? aiData.sideStoneShape : (aiData.sideStoneShape ? [aiData.sideStoneShape] : []),
          sideStoneCut: Array.isArray(aiData.sideStoneCut) ? aiData.sideStoneCut : (aiData.sideStoneCut ? [aiData.sideStoneCut] : []),
          sideStoneColour: Array.isArray(aiData.sideStoneColor) ? aiData.sideStoneColor : (aiData.sideStoneColor ? [aiData.sideStoneColor] : []),
          sideStoneClarity: Array.isArray(aiData.sideStoneClarity) ? aiData.sideStoneClarity : (aiData.sideStoneClarity ? [aiData.sideStoneClarity] : []),
          sideStoneCarat: Array.isArray(aiData.sideStoneCarat) ? parseFloat(aiData.sideStoneCarat[0]) || 0 : parseFloat(aiData.sideStoneCarat) || 0,
          noOfSideStones: Array.isArray(aiData.noOfSideStones) ? parseInt(aiData.noOfSideStones[0], 10) || 0 : parseInt(aiData.noOfSideStones, 10) || 0,
          individualCaratBreakdown: aiData.IndividualCaratBreakdown || defaultBreakdown || null,
        };
      } else {
        sideStoneFields = {
          sideStoneType: null,
          sideStoneShape: null,
          sideStoneCut: null,
          sideStoneColour: null,
          sideStoneClarity: null,
          sideStoneCarat: null,
          noOfSideStones: null,
          individualCaratBreakdown: [],
        };
      }

      const analysisResponse = {
        itemType: aiData.itemType || "Diamond",
        cut: aiData.cut || null,
        color: aiData.color || null,
        clarity: aiData.clarity || null,
        carat: parseFloat(aiData.carat) || 0,
        estimatedMarketPriceUSD: aiData.estimatedMarketPriceUSD || 0,
        confidence: aiData.confidence || "medium",
        remarks: aiData.remarks || "",
        weight: actualWeight || aiData.weight || null,
        grossWeight: actualWeight ? parseFloat(actualWeight) : null,
        netWeight: netNum != null ? Math.round(netNum * 1000) / 1000 : null,
        certificateAndReportNumber: aiData.certificateAndReportNumber || null,
        metal: aiData.metal || null,
        metalWeight:
          netNum != null
            ? Math.round(netNum * 1000) / 1000
            : aiData.metalWeight
              ? parseFloat(aiData.metalWeight)
              : null,
        centreStone: (aiData?.itemType?.toLowerCase().includes("diamond") || aiData?.category?.toLowerCase().includes("diamond") || aiData.centreStoneEnabled) ? (aiData.centreStone || null) : null,
        metalPurity: aiData.metalPurity || null,
        shape: (aiData?.itemType?.toLowerCase().includes("diamond") || aiData?.category?.toLowerCase().includes("diamond") || aiData.centreStoneEnabled) ? (aiData.shape || null) : null,
        spotPricePerGramUsd: aiData.spotPricePerGramUsd || null,
        spotPriceUpdateDate: aiData.spotPriceUpdateDate || null,
        isMetalChange: isMetalChanged(),
        originType: aiData.originType || null,
        ...sideStoneFields,
      };

      const payload = {
        jewelryAnalysisId: jewelryAnalysisId || aiData?.draftId || "",
        email: contactForm.email,
        emailAboutUpcomingAuction: contactForm.emailAboutUpcomingAuction,
        usePhotoToImproveCarrotPro: contactForm.usePhotoToImproveCarrotPro,
        analysisResponse,
      };

      publicApi
        .post(caratProSeeMyPriceUrl, payload, { timeout: 600000 })
        .then((response) => {
          if (response.status === 200) {
            if (response.data.jewelryAnalysisId) {
              setJewelryAnalysisId(response.data.jewelryAnalysisId);
            }
            setAiData((prev: any) => ({
              ...prev,
              estimatedMarketPriceUSD: response.data.averagePrice,
              minPrice: response.data.minPrice,
              maxPrice: response.data.maxPrice,
              meltValueUSD: response.data.meltValueUSD,
              comparableSalesCount: response.data.matchedCount,
              confidence: response.data.confidence,
              dataFromYear: response.data.dataFromYear,
              dataToYear: response.data.dataToYear,
              spotPricePerGramUsd: response.data.spotPricePerGramUsd,
              spotPriceUpdateDate: response.data.spotPriceUpdateDate,
              metalPurityPercentage: response.data.metalPurityPercentage,
              metalWeight:
                response.data.metalWeight ??
                response.data.metalWeightInGrams ??
                prev.metalWeight,
            }));
            setUsageData((prev: any) => ({ ...prev, limitExceeded: false }));
            localStorage.removeItem("pendingAnalysis");
            proceedToNext();
          } else {
            setIsPricing(false);
            alert("Failed to get pricing. Please try again later.");
          }
        })
        .catch((error) => {
          if (error.response?.status === 402) {
            const errors = error.response.data?.errors || [];
            const isPaymentRequired =
              errors.some(
                (e: any) =>
                  e.code === "FREE_SEARCH_EXHAUSTED" || e.code === "KB412013",
              ) || error.response?.status === 402;

            if (isPaymentRequired) {
              let jAnalysisId = null;
              let amount = 10.0;
              let currency = "USD";

              // First, try to find the KB412013 error which contains the JSON
              const jsonError = errors.find(
                (e: any) =>
                  e.code === "KB412013" ||
                  (e.field && e.field.includes("{")) ||
                  (e.message && e.message.includes("{")),
              );

              if (jsonError) {
                const jsonString =
                  jsonError.field && jsonError.field.includes("{")
                    ? jsonError.field
                    : jsonError.message;
                try {
                  const msg = JSON.parse(jsonString);
                  jAnalysisId = msg.jewelryAnalysisId || null;
                  amount = msg.amount !== undefined ? msg.amount : 10.0;
                  currency = msg.currency || "USD";
                } catch (e) {
                  // Fallback regex extraction if JSON is malformed by the backend
                  const idMatch = jsonString?.match(
                    /["']?jewelryAnalysisId["']?\s*:\s*["']([^"']+)["']/,
                  );
                  if (idMatch) {
                    jAnalysisId = idMatch[1];
                  }
                  const amountMatch = jsonString?.match(
                    /["']?amount["']?\s*:\s*([\d.]+)/,
                  );
                  if (amountMatch) {
                    amount = parseFloat(amountMatch[1]);
                  }
                  const currencyMatch = jsonString?.match(
                    /["']?currency["']?\s*:\s*["']([^"']+)["']/,
                  );
                  if (currencyMatch) {
                    currency = currencyMatch[1];
                  }
                }
              }

              // Fallback if the backend just returned the ID directly in a previous mock style
              if (
                !jAnalysisId &&
                errors.length > 0 &&
                !errors[0].message.includes(" ")
              ) {
                jAnalysisId = errors[0].message;
              }

              // Check if it's in the root of the response instead of the errors array
              if (!jAnalysisId) {
                jAnalysisId =
                  error.response.data?.jewelryAnalysisId ||
                  error.response.data?.data?.jewelryAnalysisId ||
                  null;
                if (error.response.data?.amount !== undefined) {
                  amount = error.response.data.amount;
                }
                if (error.response.data?.currency) {
                  currency = error.response.data.currency;
                }
              }

              console.log(
                "Parsed Valuation ID:",
                jAnalysisId,
                "from 402 payload:",
                error.response.data,
              );
              setJewelryAnalysisId(jAnalysisId);
              setValuationPrice(amount);
              setValuationCurrency(currency);
              setUsageData((prev: any) => ({ ...prev, limitExceeded: true }));
              localStorage.setItem(
                "pendingAnalysis",
                JSON.stringify({
                  jewelryAnalysisId: jAnalysisId,
                  draftId: aiData?.draftId || jAnalysisId || "",
                  email: contactForm.email,
                }),
              );
              proceedToNext();
            } else {
              setIsPricing(false);
              alert(
                error.response?.data?.message ||
                error.response?.data?.errors?.[0]?.message ||
                "Something went wrong. Please try again.",
              );
            }
          } else {
            console.error("See my price failed:", error);
            setIsPricing(false);
            alert(
              error.response?.data?.message ||
              error.response?.data?.errors?.[0]?.message ||
              "Failed to get pricing. Please try again.",
            );
          }
        });
    } else if (currentStep < 6) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePaywallSubmit = (option: "pay" | "consult") => {
    if (option === "pay") {
      setIsPaywallLoading(true);

      let attempts = 0;
      const maxAttempts = 20; // 40 seconds max wait time

      const pollTimer = setInterval(async () => {
        attempts++;
        try {
          if (!jewelryAnalysisId) {
            clearInterval(pollTimer);
            setIsPaywallLoading(false);
            return;
          }

          try {
            const response = await publicApi.post(
              caratProSeeMyPriceUrl,
              {
                jewelryAnalysisId: jewelryAnalysisId || aiData?.draftId || "",
                email: contactForm.email,
                emailAboutUpcomingAuction:
                  contactForm.emailAboutUpcomingAuction,
                usePhotoToImproveCarrotPro:
                  contactForm.usePhotoToImproveCarrotPro,
              },
              {
                timeout: 600000,
              },
            );

            if (response.status === 200) {
              clearInterval(pollTimer);
              if (response.data.jewelryAnalysisId) {
                setJewelryAnalysisId(response.data.jewelryAnalysisId);
              }
              setAiData((prev: any) => ({
                ...prev,
                estimatedMarketPriceUSD: response.data.averagePrice,
                minPrice: response.data.minPrice,
                maxPrice: response.data.maxPrice,
                meltValueUSD: response.data.meltValueUSD,
                comparableSalesCount: response.data.matchedCount,
                confidence: response.data.confidence,
                dataFromYear: response.data.dataFromYear,
                dataToYear: response.data.dataToYear,
                spotPricePerGramUsd: response.data.spotPricePerGramUsd,
                spotPriceUpdateDate: response.data.spotPriceUpdateDate,
                metalPurityPercentage: response.data.metalPurityPercentage,
                metalWeight:
                  response.data.metalWeight ??
                  response.data.metalWeightInGrams ??
                  prev.metalWeight,
              }));
              localStorage.removeItem("pendingAnalysis");

              setIsPaywallLoading(false);
              setShowPaywallModal(false);
              setPaymentError(null);
              setWasPaymentMade(true);
              setShowPaymentSuccessBanner(true);
              setUsageData((prev: any) => ({ ...prev, limitExceeded: false }));
              setRequestsCount(0); // Reset limit since they paid
              if (currentStep < 6) {
                setCurrentStep(currentStep + 1);
              }
            }
          } catch (error) {
            const err = error as any;
            if (err.response?.status === 402) {
              const errCode = err.response.data?.errors?.[0]?.code;
              const errMsg = err.response.data?.errors?.[0]?.message;
              if (errCode === "KB412013" && errMsg === "PAYMENT_PENDING") {
                // Keep polling
              } else {
                // Keep polling until max attempts
              }
            } else {
              console.error("Error polling see-my-price", err);
            }
          }
        } catch (error) {
          console.error("Error polling payment status", error);
        }

        if (attempts >= maxAttempts) {
          clearInterval(pollTimer);
          setIsPaywallLoading(false);
          setPaymentError(
            "Payment verification timed out. Please check your card status or try again.",
          );
        }
      }, 2000);
    } else if (option === "consult") {
      setShowPaywallModal(false);
      // Skip the rest and submit exactly what we have to the gemologist
      handleSubmitFinal("SUBMIT_TO_GEMOLOGIST");
    }
  };

  const handleSubmitFinal = async (overridePurpose?: string) => {
    // if (
    //   overridePurpose !== "SUBMIT_TO_GEMOLOGIST" &&
    //   (!askingPrice || parseFloat(askingPrice) <= 0)
    // ) {
    //   alert("Asking price must be greater than 0.");
    //   return;
    // }
    console.log("Submitting all data to backend...");
    try {
      const folderId = jewelryAnalysisId || `CP-${Date.now()}`;
      const allFiles: {
        file: File;
        path: string;
        isCert: boolean;
        isHeic: boolean;
        tempFileUrl?: string;
      }[] = [];

      const getFileInfo = (f: File, isCert: boolean) => {
        const previewItem = previewUrls.find(
          (p) =>
            p.file === f ||
            (p.file?.name === f.name && p.file?.size === f.size),
        );
        const tempFileUrl =
          (f as any).tempFileUrl || previewItem?.tempFileUrl;
        const ext = f.name.split(".").pop()?.toLowerCase() || "";
        const isHeic =
          ext === "heic" ||
          ext === "heif" ||
          f.type === "image/heic" ||
          f.type === "image/heif" ||
          Boolean(tempFileUrl) ||
          Boolean((f as any).isHeic);

        const cleanName = f.name.replace(/[^a-zA-Z0-9._-]/g, "_");
        const path = `carat-pro/${folderId}/${cleanName}`;

        return {
          file: f,
          path,
          isCert,
          isHeic,
          tempFileUrl,
        };
      };

      filesObjects.photo?.forEach((photo: File) => {
        allFiles.push(getFileInfo(photo, false));
      });

      filesObjects.certificate?.forEach((cert: File) => {
        allFiles.push(getFileInfo(cert, true));
      });

      const media: any[] = [];
      const nonHeicFiles = allFiles.filter((f) => !f.isHeic);
      const paths = nonHeicFiles.map((f) => f.path);

      let presignedUrls: any[] = [];
      if (paths.length > 0) {
        // Fetch presigned URLs from public endpoint only for non-HEIC files
        const res = await publicApi.post(awsGetUrls, { paths });
        presignedUrls = Array.isArray(res.data)
          ? res.data
          : res.data?.data || res.data?.urls || res.data?.signedUrls || [];
      }

      // Process each file (upload non-HEIC to S3, use tempFileUrl as filePath for HEIC)
      for (let i = 0; i < allFiles.length; i++) {
        const fileObj = allFiles[i];

        if (fileObj.isHeic) {
          // For HEIC files, backend will upload to S3 using tempFileUrl passed in filePath
          const finalFilePath = fileObj.tempFileUrl || "";
          if (!finalFilePath) {
            console.error(`Missing tempFileUrl for HEIC file ${fileObj.file.name}`);
            throw new Error(
              `Conversion URL is missing for ${fileObj.file.name}. Please re-upload this file.`,
            );
          }

          media.push({
            filePath: finalFilePath,
            name: fileObj.isCert ? "Cert" : "Photo",
            originalName: fileObj.file.name,
            mediaType: fileObj.isCert ? "DOCUMENT" : "PHOTO",
            primary: media.length === 0 && !fileObj.isCert,
          });
        } else {
          // Upload non-HEIC file to its presigned URL
          const match =
            presignedUrls.find((u: any) => u.path === fileObj.path) ||
            presignedUrls[nonHeicFiles.indexOf(fileObj)];
          const uploadUrl =
            match?.presignedUrl || match?.url || match?.signedUrl;

          if (uploadUrl) {
            // First attempt: upload without Content-Type (prevents S3 signature mismatch 403)
            let uploadRes = await fetch(uploadUrl, {
              method: "PUT",
              body: fileObj.file,
            });

            // Second attempt: if S3 returned 403, retry with Content-Type header
            if (uploadRes.status === 403 && fileObj.file.type) {
              console.warn(
                `S3 403 on initial upload for ${fileObj.file.name}, retrying with Content-Type...`,
              );
              uploadRes = await fetch(uploadUrl, {
                method: "PUT",
                body: fileObj.file,
                headers: { "Content-Type": fileObj.file.type },
              });
            }

            if (!uploadRes.ok) {
              console.error(
                `S3 upload failed for ${fileObj.file.name}: ${uploadRes.status} ${uploadRes.statusText}`,
              );
              throw new Error(
                `Failed to upload ${fileObj.file.name} to storage (Status ${uploadRes.status}).`,
              );
            }

            media.push({
              filePath: fileObj.path,
              name: fileObj.isCert ? "Cert" : "Photo",
              originalName: fileObj.file.name,
              mediaType: fileObj.isCert ? "DOCUMENT" : "PHOTO",
              primary: media.length === 0 && !fileObj.isCert,
            });
          } else {
            console.error(`No presigned URL returned for ${fileObj.path}`);
            throw new Error(
              `Failed to obtain upload permission for ${fileObj.file.name}`,
            );
          }
        }
      }

      // If no photo was marked primary, mark the first one as primary
      if (media.length > 0 && !media.some((m) => m.primary)) {
        media[0].primary = true;
      }

      const purposeMap: any = {
        consign: "CONSIGN_TO_ZIPANG_AUCTION",
        live: "LIST_ON_JEWEL_LIVE",
        valuation: "GET_CERTIFIED_VALUATION",
        none_of_the_above: "NONE_OF_THE_ABOVE",
      };

      const itemTypeVal = (aiData.itemType || "").trim();
      const categoryVal = (aiData.category || "").trim();

      let constructedItemName = itemTypeVal || categoryVal || "Jewelry";
      if (
        itemTypeVal &&
        categoryVal &&
        !itemTypeVal.toLowerCase().includes(categoryVal.toLowerCase()) &&
        categoryVal.toLowerCase() !== "jewelry"
      ) {
        constructedItemName = `${itemTypeVal} ${categoryVal}`;
      }

      const payload = {
        jewelryAnalysisId: jewelryAnalysisId || aiData?.draftId || "",
        purpose:
          overridePurpose ||
          purposeMap[sellOption] ||
          null,
        itemName: constructedItemName,
        origin: Boolean(aiData?.originConfirmed || aiData?.origin),
        originType: aiData?.originType || "",
        lab: aiData.lab || "",
        askingPriceUSD: parseFloat(askingPrice) || 0,
        description: contactForm.message || aiData.description || "",
        media: media,
      };

      await publicApi.post(caratProSubmissionUrl, payload);
      console.log("Submitted payload:", payload);

      setShowSuccessModal(true);
    } catch (error) {
      console.error("Submission failed", error);
      alert("Failed to upload media or submit form. Please try again.");
    }
  };

  const handlePrev = () => {
    if (currentStep === 6) {
      const confirmBack = window.confirm(
        "Changes that you made may not be saved. Are you sure you want to go back?"
      );
      if (!confirmBack) {
        return;
      }
    }
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setShowPaymentSuccessBanner(false);
    }
  };

  const handleFileChange = async (e: any) => {
    setExtractError(null);
    const targetInput = e.target;
    const fieldName = targetInput.name as string;
    const _files = Array.from(e.target.files as FileList);
    const newFileNames: string[] = [];
    const newPreviewUrls: any[] = [];
    const validFiles: File[] = [];
    const existingFiles = filesObjects[fieldName] || [];

    const MAX_TOTAL_SIZE = 25 * 1024 * 1024; // 25 MB total limit
    const allowedExtensions = [
      "png",
      "jpg",
      "jpeg",
      "jfif",
      "heic",
      "heif",
      "webp",
    ];
    const allowedMimeTypes = [
      "image/png",
      "image/jpeg",
      "image/jpg",
      "image/pjpeg",
      "image/jfif",
      "image/webp",
      "image/heic",
      "image/heif",
    ];

    const existingTotalSize = [
      ...(filesObjects.photo || []),
      ...(filesObjects.certificate || []),
    ].reduce((sum: number, f: File) => sum + (f.size || 0), 0);

    let accumulatedSize = existingTotalSize;
    const validInitialFiles: File[] = [];

    for (let file of _files) {
      const fileExtension = file.name.split(".").pop()?.toLowerCase() || "";
      const isAllowedExt = allowedExtensions.includes(fileExtension);
      const isAllowedMime = file.type
        ? allowedMimeTypes.includes(file.type.toLowerCase())
        : isAllowedExt;

      if (!isAllowedExt || !isAllowedMime) {
        setExtractError(
          `File type not supported for ${file.name}. Only .png, .jpg, .jpeg, .jfif, .heic, .heif, and .webp images are allowed.`,
        );
        continue;
      }

      if (accumulatedSize + file.size > MAX_TOTAL_SIZE) {
        setExtractError(
          `Total file size exceeds 25 MB limit. ${file.name} could not be added.`,
        );
        continue;
      }

      const isDuplicate =
        existingFiles.some(
          (f: File) => f.name === file.name && f.size === file.size,
        ) ||
        validInitialFiles.some(
          (f: File) => f.name === file.name && f.size === file.size,
        );

      if (isDuplicate) {
        continue; // Skip duplicate files
      }

      if (previewUrls.length + validInitialFiles.length >= 10) {
        setExtractError("You can only upload up to 10 files maximum.");
        break; // Ignore the rest
      }

      accumulatedSize += file.size;
      validInitialFiles.push(file);
    }

    if (validInitialFiles.length === 0) {
      targetInput.value = "";
      return; // Nothing new to add
    }

    const heicFiles = validInitialFiles.filter((f) => {
      const ext = f.name.split(".").pop()?.toLowerCase() || "";
      return (
        ext === "heic" ||
        ext === "heif" ||
        f.type === "image/heic" ||
        f.type === "image/heif"
      );
    });

    let convertedHeicData: any[] = [];
    if (heicFiles.length > 0) {
      setExtractError("Please wait, generating previews...");
      const formData = new FormData();
      heicFiles.forEach((f) => formData.append("files", f));
      try {
        const res = await publicApi.post(convertHeicToJpgUrl, formData, {
          timeout: 600000,
        });
        if (Array.isArray(res.data)) {
          convertedHeicData = res.data;
        } else if (Array.isArray(res.data?.data)) {
          convertedHeicData = res.data.data;
        }
        setExtractError(null);
      } catch (error) {
        console.error("HEIC conversion failed:", error);
        setExtractError(
          "Failed to process HEIC images. Please try again with standard JPEGs.",
        );
        targetInput.value = "";
        return;
      }
    }

    for (let file of validInitialFiles) {
      const fileExtension = file.name.split(".").pop()?.toLowerCase() || "";
      const isHeic =
        fileExtension === "heic" ||
        fileExtension === "heif" ||
        file.type === "image/heic" ||
        file.type === "image/heif";

      let finalPreviewUrl = URL.createObjectURL(file);
      let matched: any = null;

      if (isHeic && convertedHeicData.length > 0) {
        // Try to match by filename prefix, fallback to shifting the first available
        const originalBase = file.name.substring(0, file.name.lastIndexOf("."));
        const matchedIndex = convertedHeicData.findIndex(
          (d: any) =>
            d.fileName &&
            (d.fileName.toLowerCase().startsWith(originalBase.toLowerCase()) ||
              d.fileName.toLowerCase().includes(originalBase.toLowerCase())),
        );

        if (matchedIndex !== -1) {
          matched = convertedHeicData.splice(matchedIndex, 1)[0];
        } else {
          matched = convertedHeicData.shift();
        }

        if (matched) {
          if (matched.tempThumbFileUrl) {
            finalPreviewUrl = matched.tempThumbFileUrl;
          } else if (matched.tempFileUrl) {
            finalPreviewUrl = matched.tempFileUrl;
          } else if (matched.base64Image) {
            finalPreviewUrl = matched.base64Image.startsWith("data:")
              ? matched.base64Image
              : `data:${matched.mimeType || "image/jpeg"};base64,${matched.base64Image}`;
          }
        }
      }

      if (matched?.tempFileUrl) {
        (file as any).tempFileUrl = matched.tempFileUrl;
        (file as any).tempThumbFileUrl = matched.tempThumbFileUrl;
      }
      (file as any).isHeic = isHeic;

      validFiles.push(file);
      newFileNames.push(file.name);

      if (fieldName === "photo" || fieldName === "certificate") {
        newPreviewUrls.push({
          url: finalPreviewUrl,
          type: fieldName,
          file,
          tempFileUrl: matched?.tempFileUrl,
          tempThumbFileUrl: matched?.tempThumbFileUrl,
        });
      }
    }

    files[fieldName] = [...files[fieldName], ...newFileNames];
    filesObjects[fieldName] = [...existingFiles, ...validFiles] as File[];
    setFiles({ ...files });

    if (
      (fieldName === "photo" || fieldName === "certificate") &&
      newPreviewUrls.length > 0
    ) {
      setPreviewUrls((prev) => [...prev, ...newPreviewUrls].slice(0, 10));

      if (activeCardId !== null) {
        setCardPreviews((prev) => ({
          ...prev,
          [activeCardId]: newPreviewUrls[0].url,
        }));
        setActiveCardId(null);
      }
    }

    // Reset the input value so the same file can be selected again
    targetInput.value = "";
  };

  const handleRemovePhoto = React.useCallback((indexToRemove: number) => {
    setPreviewUrls((prev) => {
      const itemToRemove = prev[indexToRemove];
      if (itemToRemove && itemToRemove.type) {
        filesObjects[itemToRemove.type] = filesObjects[
          itemToRemove.type
        ].filter((f: any) => f !== itemToRemove.file);
        setFiles((prevFiles: any) => ({
          ...prevFiles,
          [itemToRemove.type]: prevFiles[itemToRemove.type].filter(
            (name: string) => name !== itemToRemove.file.name,
          ),
        }));
      }

      const newUrls = prev.filter((_, index) => index !== indexToRemove);
      if (newUrls.length < 10) {
        setExtractError(null);
      }
      return newUrls;
    });
  }, []);

  const handleLogin = () => {
    let languageCode =
      localStorage.getItem("language") === "EN"
        ? "en"
        : localStorage.getItem("language") === "zh_CN"
          ? "zh_CN"
          : "jp";
    const myPageRedirectionPath = `/${languageCode}${"/my-page"}`;
    localStorage.setItem("postLoginRedirect", myPageRedirectionPath);
    localStorage.setItem("openInventoryPopup", "true");
    setShowSignIn(true);
  };

  const handleClose = () => {
    localStorage.removeItem("postLoginRedirect");
    localStorage.removeItem("openInventoryPopup");
    setShowSignIn(false);
  };

  return (
    <DialogProvider>
      <Prompt
        when={currentStep === 6 && !showSuccessModal}
        message="Changes that you made may not be saved. Are you sure you want to leave?"
      />
      <ZipangHeader />
      <PageContainer>
        <ScrollFix />

        <SellAiHero
          onStartValuation={() => {
            const wizard = document.getElementById("wizard-section");
            wizard?.scrollIntoView({ behavior: "smooth" });
          }}
          onLogin={handleLogin}
        />

        <WizardSection id="wizard-section">
          <WizardContainer>
            <div style={{ textAlign: "left", marginBottom: "2rem" }}>
              <h2
                style={{
                  fontSize: "1.75rem",
                  fontFamily: "Playfair Display, serif",
                  fontWeight: 700,
                  margin: "0 0 0.5rem 0",
                  color: "#1A1A1A",
                }}
              >
                Value your piece
              </h2>
              <p style={{ color: "#666", margin: "0 0 1rem 0" }}>
                Five short steps — upload, confirm, weight, price, submit.
              </p>
              <div
                style={{
                  display: "inline-block",
                  background: "#FFF0F2",
                  padding: "0.5rem 1rem",
                  borderRadius: "24px",
                  fontSize: "0.65rem",
                  fontWeight: 600,
                  color: "#d92b3c",
                  letterSpacing: "1.5px",
                  border: "1px solid #FCEBEA",
                  textTransform: "uppercase",
                }}
              >
                ● Only 2 valuations free
              </div>
            </div>

            <StepIndicatorContainer>
              {[
                { id: 1, label: "Photos" },
                { id: 2, label: "Confirm" },
                { id: 3, label: "Weight" },
                { id: 4, label: "Preview" },
                { id: 5, label: "Details" },
                { id: 6, label: "Price" },
              ].map((step) => {
                const isActive = currentStep === step.id;
                const isPast = currentStep > step.id;

                const textColor = isActive || isPast ? "#1A1A1A" : "#999";
                const dotColor = isActive
                  ? "#d92b3c"
                  : isPast
                    ? "#1A1A1A"
                    : "#999";
                const lineColor = isActive
                  ? "#d92b3c"
                  : isPast
                    ? "#d92b3c"
                    : "#EAEAEA";
                const lineHeight = isActive ? "2px" : "1px";

                return (
                  <StepIndicatorItem key={step.id}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        color: textColor,
                        fontSize: "0.85rem",
                        fontWeight: isActive ? 700 : 500,
                      }}
                    >
                      <span
                        style={{
                          fontSize: "1.2rem",
                          lineHeight: 1,
                          color: dotColor,
                        }}
                      >
                        •
                      </span>
                      {step.label}
                    </div>
                    <div
                      style={{
                        width: "100%",
                        height: lineHeight,
                        background: lineColor,
                        transition: "all 0.3s",
                      }}
                    />
                  </StepIndicatorItem>
                );
              })}
            </StepIndicatorContainer>

            {showSuccessModal ? (
              <SuccessView email={contactForm.email} usageData={usageData} />
            ) : (
              <>
                {currentStep > 1 && (
                  <UploadedImagePreviews
                    previewUrls={previewUrls}
                    handleRemovePhoto={handleRemovePhoto}
                    showRemoveButton={false}
                  />
                )}
                <form
                  ref={formRef}
                  noValidate
                  autoComplete="off"
                  onSubmit={(e) => e.preventDefault()}
                >
                  <StepContent>
                    {currentStep === 1 && (
                      <WizardStep1Photos
                        photoRef={photoRef}
                        certificateRef={certificateRef}
                        handleFileChange={handleFileChange}
                        previewUrls={previewUrls}
                        handleRemovePhoto={handleRemovePhoto}
                        isExtracting={isExtracting}
                        extractError={extractError}
                      />
                    )}
                    {currentStep === 2 && (
                      <WizardStep2AIRead
                        aiData={aiData}
                        setAiData={setAiData}
                        initialAiData={initialAiData}
                        isSubmitting={isMatchingSales}
                        validationErrors={validationErrors}
                      />
                    )}
                    {currentStep === 3 && (
                      <WizardStep3Weight
                        actualWeight={actualWeight}
                        setActualWeight={setActualWeight}
                        aiData={aiData}
                        setAiData={setAiData}
                        validationErrors={validationErrors}
                        previewUrls={previewUrls}
                      />
                    )}
                    {currentStep === 4 && (
                      <WizardStep4Preview
                        aiData={aiData}
                        actualWeight={actualWeight}
                      />
                    )}
                    {currentStep === 5 && (
                      <WizardStep5Details
                        contactForm={contactForm}
                        handleContactFormChange={handleContactFormChange}
                        isEmailVerified={isEmailVerified}
                        setIsEmailVerified={setIsEmailVerified}
                        isPhoneVerified={isPhoneVerified}
                        setIsPhoneVerified={setIsPhoneVerified}
                        usageData={usageData}
                        setUsageData={setUsageData}
                      />
                    )}
                    {currentStep === 6 && usageData?.limitExceeded ? (
                      <div
                        style={{
                          background: "#fff",
                          borderRadius: "12px",
                          padding: "4rem 2rem",
                          color: "#1A1A1A",
                          textAlign: "center",
                          border: "1px solid #EAEAEA",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
                          marginTop: "2rem",
                        }}
                      >
                        {paymentError && (
                          <div
                            style={{
                              background: "#FEF2F2",
                              border: "1px solid #F87171",
                              color: "#991B1B",
                              padding: "1rem 1.5rem",
                              borderRadius: "8px",
                              marginBottom: "1.5rem",
                              fontWeight: 600,
                              display: "flex",
                              alignItems: "center",
                              gap: "0.75rem",
                              textAlign: "left",
                            }}
                          >
                            <span style={{ fontSize: "1.25rem" }}>✕</span>
                            {paymentError}
                          </div>
                        )}
                        <div
                          style={{
                            color: "#d92b3c",
                            margin: "0 auto 1rem auto",
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            height="24"
                            width="24"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                          >
                            <path d="M12 2L2 12l10 10 10-10L12 2z" />
                          </svg>
                        </div>
                        <div
                          style={{
                            fontSize: "1.75rem",
                            fontWeight: "bold",
                            fontFamily: '"Playfair Display", serif',
                            marginBottom: "1rem",
                          }}
                        >
                          Your price is ready
                        </div>
                        <div
                          style={{
                            color: "#666",
                            fontSize: "1rem",
                            lineHeight: "1.6",
                            maxWidth: "450px",
                            margin: "0 auto 2.5rem auto",
                          }}
                        >
                          You've used both free valuations. Unlock this one for{" "}
                          {valuationCurrency === "USD"
                            ? "$"
                            : valuationCurrency}
                          {valuationPrice} — or skip it: submit the piece and
                          our gemologist gives you a firm offer, free.
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            justifyContent: "center",
                            gap: "1rem",
                            marginBottom: "2rem",
                          }}
                        >
                          <button
                            onClick={async (e) => {
                              e.preventDefault();
                              setIsPaywallLoading(true);
                              setPaymentError(null);
                              try {
                                if (jewelryAnalysisId) {
                                  const res = await publicApi.post(
                                    caratProPaymentCreateIntentUrl,
                                    {
                                      email: contactForm.email,
                                      jewelryAnalysisId: jewelryAnalysisId,
                                    },
                                  );
                                  if (res.data?.clientSecret) {
                                    setClientSecret(res.data.clientSecret);
                                    setShowPaywallModal(true);
                                  } else {
                                    setPaymentError(
                                      "Unable to initialize payment. Please try again later.",
                                    );
                                  }
                                } else {
                                  setPaymentError(
                                    "Valuation ID is missing. Please restart the valuation process.",
                                  );
                                }
                              } catch (error) {
                                console.error("Failed to create intent", error);
                                const err = error as any;
                                const errMsg =
                                  err?.response?.data?.message ||
                                  err?.response?.data?.error ||
                                  err?.message ||
                                  "";
                                if (
                                  typeof errMsg === "string" &&
                                  (errMsg.toLowerCase().includes("live charges") ||
                                    errMsg.toLowerCase().includes("cannot currently make live charges") ||
                                    errMsg.toLowerCase().includes("livemode_charges_disabled"))
                                ) {
                                  setPaymentError(
                                    "We are currently not accepting live payments. Please contact the administrator for any concern - contact@myauctions.jp",
                                  );
                                } else {
                                  setPaymentError(
                                    "Failed to initialize payment securely. Please try again.",
                                  );
                                }
                              } finally {
                                setIsPaywallLoading(false);
                              }
                            }}
                            style={{
                              background: "#d92b3c",
                              color: "#fff",
                              border: "none",
                              borderRadius: "8px",
                              padding: "0.85rem 2rem",
                              fontWeight: "bold",
                              fontSize: "0.95rem",
                              cursor: "pointer",
                            }}
                          >
                            Unlock for{" "}
                            {valuationCurrency === "USD"
                              ? "$"
                              : valuationCurrency}
                            {valuationPrice}
                          </button>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              handlePaywallSubmit("consult");
                            }}
                            style={{
                              background: "#fff",
                              color: "#d92b3c",
                              border: "1.5px solid #d92b3c",
                              borderRadius: "8px",
                              padding: "0.85rem 2rem",
                              fontWeight: "bold",
                              fontSize: "0.95rem",
                              cursor: "pointer",
                            }}
                          >
                            Submit to gemologist — free
                          </button>
                        </div>
                        <div
                          style={{
                            color: "#999",
                            fontSize: "0.75rem",
                            lineHeight: "1.5",
                            maxWidth: "300px",
                            margin: "0 auto",
                          }}
                        >
                          Charged only when the price is delivered. Talking to a
                          gemologist never costs anything.
                        </div>
                      </div>
                    ) : (
                      currentStep === 6 && (
                        <>
                          {wasPaymentMade && showPaymentSuccessBanner && (
                            <div
                              style={{
                                background: "#ECFDF5",
                                border: "1px solid #10B981",
                                color: "#065F46",
                                padding: "1rem 1.5rem",
                                borderRadius: "8px",
                                marginBottom: "1.5rem",
                                fontWeight: 600,
                                display: "flex",
                                alignItems: "center",
                                gap: "0.75rem",
                              }}
                            >
                              <span style={{ fontSize: "1.25rem" }}>✓</span>
                              Payment successful! Your Carat Pro valuation price
                              has been unlocked.
                            </div>
                          )}
                          {paymentError && (
                            <div
                              style={{
                                background: "#FEF2F2",
                                border: "1px solid #F87171",
                                color: "#991B1B",
                                padding: "1rem 1.5rem",
                                borderRadius: "8px",
                                marginBottom: "1.5rem",
                                fontWeight: 600,
                                display: "flex",
                                alignItems: "center",
                                gap: "0.75rem",
                              }}
                            >
                              <span style={{ fontSize: "1.25rem" }}>✕</span>
                              {paymentError}
                            </div>
                          )}
                          <WizardStep6Price
                            aiData={aiData}
                            sellOption={sellOption}
                            setSellOption={setSellOption}
                            askingPrice={askingPrice}
                            setAskingPrice={setAskingPrice}
                          />
                        </>
                      )
                    )}
                  </StepContent>
                </form>

                <StepActions style={{ alignItems: "center" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      color: "#666",
                      fontSize: "0.85rem",
                    }}
                  >
                    <span style={{ fontSize: "0.8rem" }}>🔒</span>
                    Your photos stay private until you submit.
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "1rem",
                      marginLeft: "auto",
                    }}
                  >
                    <div
                      onClick={currentStep > 1 ? handlePrev : undefined}
                      style={{
                        padding: "0.75rem 1.5rem",
                        fontSize: "0.9rem",
                        fontWeight: "bold",
                        color: currentStep === 1 ? "#ccc" : "#1A1A1A",
                        cursor: currentStep === 1 ? "not-allowed" : "pointer",
                        border: "1px solid",
                        borderColor: currentStep === 1 ? "#EAEAEA" : "#CCC",
                        borderRadius: "8px",
                        background: "#fff",
                      }}
                    >
                      Back
                    </div>

                    {!(currentStep === 6 && usageData?.limitExceeded) &&
                      ((currentStep === 1 && isExtracting) ||
                        (currentStep === 2 && isMatchingSales) ||
                        (currentStep === 5 && isPricing) ? (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.75rem",
                            color: "#d92b3c",
                            fontWeight: 700,
                            fontSize: "0.65rem",
                            letterSpacing: "1px",
                            padding: "0 1rem",
                          }}
                        >
                          {(currentStep === 2 || currentStep === 5) && (
                            <svg
                              viewBox="0 0 50 50"
                              style={{
                                width: "18px",
                                height: "18px",
                                animation: "spin 1s linear infinite",
                              }}
                            >
                              <circle
                                cx="25"
                                cy="25"
                                r="20"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="5"
                                strokeDasharray="30 100"
                                strokeLinecap="round"
                              ></circle>
                              <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
                            </svg>
                          )}
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              lineHeight: 1.1,
                              textAlign: currentStep === 1 ? "right" : "left",
                            }}
                          >
                            <span>
                              {currentStep === 1
                                ? "READING"
                                : currentStep === 2
                                  ? "MATCHING CLEARED"
                                  : "PRICING YOUR"}
                            </span>
                            <span>
                              {currentStep === 1
                                ? "PHOTOS"
                                : currentStep === 2
                                  ? "SALES"
                                  : "PIECE"}
                            </span>
                          </div>
                          {currentStep === 1 && (
                            <svg
                              viewBox="0 0 50 50"
                              style={{
                                width: "18px",
                                height: "18px",
                                animation: "spin 1s linear infinite",
                              }}
                            >
                              <circle
                                cx="25"
                                cy="25"
                                r="20"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="5"
                                strokeDasharray="30 100"
                                strokeLinecap="round"
                              ></circle>
                              <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
                            </svg>
                          )}
                        </div>
                      ) : (
                        <div
                          style={{
                            position: "relative",
                            display: "inline-block",
                            cursor:
                              (currentStep === 5 &&
                                (!contactForm.name ||
                                  !contactForm.email ||
                                  !isEmailVerified ||
                                  (contactForm.phone && !isPhoneVerified))) ||
                                (currentStep === 2 &&
                                  checkProductHasStone(aiData) &&
                                  (!aiData?.originConfirmed || !aiData?.originType))
                                ? "not-allowed"
                                : "pointer",

                            opacity:
                              (currentStep === 5 &&
                                (!contactForm.name ||
                                  !contactForm.email ||
                                  !isEmailVerified ||
                                  (contactForm.phone && !isPhoneVerified))) ||
                                (currentStep === 2 &&
                                  checkProductHasStone(aiData) &&
                                  (!aiData?.originConfirmed || !aiData?.originType))
                                ? 0.5
                                : 1,
                          }}
                        >
                          <Button
                            type="contained-red"
                            disabled={Boolean(
                              (currentStep === 1 &&
                                (!filesObjects.photo ||
                                  filesObjects.photo.length === 0)) ||
                              (currentStep === 5 &&
                                (!contactForm.name ||
                                  !contactForm.email ||
                                  !isEmailVerified ||
                                  (contactForm.phone && !isPhoneVerified))) ||
                              (currentStep === 6 && askingPrice && parseInt(askingPrice, 10) <= 0) ||
                              (currentStep === 2 &&
                                checkProductHasStone(aiData) &&
                                (!aiData?.originConfirmed || !aiData?.originType)),
                            )}
                            onClick={
                              currentStep === 6
                                ? () => handleSubmitFinal()
                                : handleNext
                            }
                            widePadding
                          >
                            {currentStep === 1
                              ? "Read my photos \u2192"
                              : currentStep === 2
                                ? "Confirm details \u2192"
                                : currentStep === 3
                                  ? "Continue \u2192"
                                  : currentStep === 4
                                    ? "Continue to details \u2192"
                                    : currentStep === 5
                                      ? "See my price \u2192"
                                      : currentStep === 6
                                        ? "Submit to gemologist"
                                        : "Continue \u2192"}
                          </Button>
                        </div>
                      ))}
                  </div>
                </StepActions>
              </>
            )}
          </WizardContainer>
        </WizardSection>

        <PaywallModal
          open={showPaywallModal}
          onClose={() => !isPaywallLoading && setShowPaywallModal(false)}
          onSubmit={handlePaywallSubmit}
          isLoading={isPaywallLoading}
          price={valuationPrice}
          currency={valuationCurrency}
          clientSecret={clientSecret || ""}
        />

        <GoogleReCaptchaModal
          open={showCaptchaModal}
          onClose={() => setShowCaptchaModal(false)}
          onSuccess={handleCaptchaSuccess}
        />
      </PageContainer>
      <Footer />
      <MobileSignIn show={showSignIn} onClose={handleClose} />
    </DialogProvider>
  );
};

export default SellAiPage;
